import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { Order, TransactionHistory } from "../../../../store/types";
import { useMemo } from "react";
import { addToCart } from "../../../../store/actions";

export type TableActionProps = React.FC<{
  order: TransactionHistory["orders"][number];
  orderIndex: number;
  isPending: boolean;
  isDotNet: boolean;
  isCart: boolean;
  length: number;
  item: TransactionHistory;
}>;

type TableProps = React.ComponentProps<TableActionProps>;

interface RouteParamDotNet {
  transaction: {
    ihno: string;
    fund: string;
    trtype: string;
  };
}

interface RouteParamCart {
  ihno: string;
  fundCode: string;
}

type GetActionText = (args: {
  isPending: boolean;
  item: TransactionHistory;
  order: Order;
}) => string;

type GetActionLink = (
  args: Order,
  isDotNet: boolean
) => {
  route: string;
  routeParams: RouteParamCart | RouteParamDotNet;
};

type GetAction = (args: {
  props: TableProps;
  history: ReturnType<typeof useHistory>;
  dispatch: ReturnType<typeof useDispatch>;
}) => () => void;

export function useTableAction(props: React.ComponentProps<TableActionProps>) {
  const history = useHistory();
  const dispatch = useDispatch();

  const { isPending, order, item } = props;

  const actionLabel = useMemo(
    () => getActionText({ isPending, order, item }),
    [item.dco_ihno]
  );

  const action = getAction({ props, history, dispatch });

  return {
    actionLabel,
    orderIndex: props.orderIndex,
    isCart: Boolean(item.dco_cart_id) || item.orders.length > 1,
    tableAction: action,
    rowSpan: item.orders.length,
  };
}

const TRANSACTIONS_MAP = [
  {
    route: "/re-initiate/swp",
    type: ["swp", "SWP"],
  },
  {
    route: "/re-initiate/stp",
    type: ["stp", "STP"],
  },
  {
    route: "/re-initiate/redemption",
    type: ["red", "RED", "REDEMPTION"],
  },
  {
    route: "/re-initiate/switch",
    type: ["switch", "SWITCH"],
  },
  {
    route: "/re-initiate/additional-purchase",
    type: [
      "APL",
      "apl",
      "Additional Purchase",
      "ADD",
      "add",
      "Additional Purchase Lumpsum",
    ],
  },
  {
    route: "/re-initiate/new-purchase",
    type: [
      "NPL",
      "npl",
      "New Purchase",
      "NEW PURCHASE",
      "NEW",
      "New Purchase Lumpsum",
    ],
  },
  {
    route: "/re-initiate/sip",
    type: [
      "SIN",
      "SINI",
      "ISIP",
      "SIPN",
      "Additional Purchase SIP",
      "Additional Purchase SIP Lumpsum",
      "New Purchase SIP",
    ],
  },
];

const getActionText: GetActionText = function (args) {
  const { isPending, item, order } = args;
  const link = order.Link || order.link;
  const { dco_ihno } = order;

  const isValidDotNetTransaction = [
    "PURCHASE",
    "SIP PAUSE",
    "REDEMPTION",
    "SWITCH",
    "NEW PURCHASE",
    "SIP CANCELLATION",
  ].includes(order.investment_type?.toUpperCase());

  if (isPending && Boolean(link)) {
    return "Copy Link";
  }
  if (isPending) {
    return "NA";
  }
  if (!dco_ihno && !item.dco_cart_id) {
    return "NA";
  }

  if (Boolean(item.dco_cart_id) || item.orders.length > 1) {
    return "Add to Cart";
  }

  if (item.isDotNet && isValidDotNetTransaction) {
    return "re-initiate";
  }

  if (
    item.orders.length === 1 &&
    item.isDotNet === false &&
    !Boolean(item.dco_cart_id)
  ) {
    return "Re-initiate";
  }

  return "NA";
};

const getActionLink: GetActionLink = function (args, isDotNet) {
  const { dco_fund, dco_ihno, investment_type } = args;

  const isValidDotNetTransaction = [
    "PURCHASE",
    "SIP PAUSE",
    "REDEMPTION",
    "SWITCH",
    "NEW PURCHASE",
    "SIP CANCELLATION",
  ].includes(investment_type?.toUpperCase());

  if (isDotNet && isValidDotNetTransaction) {
    return {
      route: "/dashboard/re-initiate-transaction",
      routeParams: {
        transaction: {
          fund: dco_fund,
          ihno: dco_ihno,
          trtype: investment_type,
        },
      },
    };
  }

  const { route } = TRANSACTIONS_MAP.find((tr) =>
    tr.type.map((s) => s.toUpperCase()).includes(investment_type.toUpperCase())
  );
  return {
    route,
    routeParams: {
      fundCode: dco_fund,
      ihno: dco_ihno,
    },
  };
};

const getAction: GetAction = function (args) {
  const { props, history, dispatch } = args;
  const { order, isDotNet, item } = props;

  async function reInitiate() {
    const { route, routeParams } = getActionLink(order, isDotNet);
    history.push(route, routeParams);
  }

  function copyLink() {
    try {
      const link = order.link || order.Link;
      navigator.clipboard.writeText(link);
      alert("Link copied !!");
    } catch (error) {
      alert(error);
      console.trace(error);
    }
  }

  async function reInitiateCart() {
    try {
      const { orders } = item;
      if (orders.length) {
        const response = await addToCart({
          orderIds: orders.map((order) => order.orderId),
          ihno: "",
        })(dispatch);
        history.push({
          pathname: "/cart/investor-cart",
          state: { pan: response.pan },
        });
        return;
      }
      const response = await addToCart({
        orderIds: [],
        ihno: item.dco_ihno,
      })(dispatch);
      history.push({
        pathname: "/cart/investor-pan",
        state: { pan: response.pan },
      });
    } catch (error) {
      alert(error);
    }
  }

  if (props.isPending) {
    return copyLink;
  }

  if (item.dco_cart_id || item.orders.length > 1) {
    return reInitiateCart;
  }

  if (item.orders.length === 1) {
    return reInitiate;
  }

  return function () {
    alert("Invalid Action");
  };
};
